import { nestCall, NestApiType } from '@/composables/nestApi';
import type { ConsentAllOptions } from './types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Mapping for a single field (used by contact and custom_data imports)
 */
export interface FieldMappingDto {
  source: string;
  target: string;
}

/**
 * Contact import request body
 * - id_shop_list: Target list ID
 * - field_mappings: Column to field mappings
 * - consent_all: Optional consent options when consent is not mapped
 */
export interface ContactInitRequestBody {
  id_shop_list: string;
  field_mappings: FieldMappingDto[];
  consent_all?: ConsentAllOptions;
  id_shop_vapid_key?: string;
}

/**
 * Custom data import request body
 * - id_custom_data_definition: Custom schema ID
 * - field_mappings: Column to field mappings
 */
export interface CustomDataInitRequestBody {
  id_custom_data_definition: string;
  field_mappings: FieldMappingDto[];
}

/**
 * Selection-based import request body (campaign_automation, campaign_bulk, template)
 * - selected_ids: Array of IDs to import
 */
export interface SelectionInitRequestBody {
  selected_ids: (string | number)[];
}

/**
 * Union type for all import request bodies
 */
export type ImportInitRequestBody =
  | ContactInitRequestBody
  | CustomDataInitRequestBody
  | SelectionInitRequestBody;

/**
 * API error response structure
 */
export interface ImportApiErrorResponse {
  statusCode: number;
  message: string;
  error: string;
}

/**
 * Init API success response data
 */
export interface InitResponseData {
  jobId: string;
  uploadUrl: string;
  uploadToken: string;
  expiresAt: string;
}

/**
 * Init API success response
 */
export interface InitSuccessResponse {
  statusCode: 200;
  data: InitResponseData;
}

/**
 * Upload API success response
 */
export interface UploadSuccessResponse {
  success: true;
  message: string;
  jobId: number;
  totalRows: number;
}

/**
 * Generic result type for API calls
 */
export type ImportResult<T> =
  | { success: true; data: T }
  | { success: false; error: string };

// =============================================================================
// IMPORT SERVICE
// =============================================================================

/**
 * Initialize an import job
 *
 * @param importType - Type of import (contact, custom_data, etc.)
 * @param body - Request body specific to the import type
 * @param additionalHeaders - Additional headers to include
 * @returns Result with upload URL or error
 */
export async function initImport<T extends ImportInitRequestBody>(
  importType: string,
  body: T,
  additionalHeaders: Record<string, string | number> = {},
): Promise<ImportResult<InitResponseData>> {
  try {
    const headers = {
      'import-type': importType,
      ...additionalHeaders,
    };

    const response = await nestCall(
      'post',
      NestApiType.CLIENT_API,
      '/job/import/init',
      headers,
      '',
      body,
      true,
    );

    // Check for error response
    if (response?.statusCode && response.statusCode !== 200) {
      return {
        success: false,
        error: response.message || 'Unknown error during import initialization',
      };
    }

    // Check for success response with data
    if (response?.data?.uploadUrl) {
      return {
        success: true,
        data: {
          jobId: response.data.jobId,
          uploadUrl: response.data.uploadUrl,
          uploadToken: response.data.uploadToken,
          expiresAt: response.data.expiresAt,
        },
      };
    }

    // Legacy response format (direct data without wrapper)
    if (response?.uploadUrl) {
      return {
        success: true,
        data: {
          jobId: response.jobId,
          uploadUrl: response.uploadUrl,
          uploadToken: response.uploadToken,
          expiresAt: response.expiresAt,
        },
      };
    }

    return {
      success: false,
      error: 'Invalid response from server',
    };
  } catch (error) {
    console.error('Import init error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to initialize import',
    };
  }
}

/**
 * Upload a file to the import upload URL
 *
 * @param uploadUrl - URL to upload the file to
 * @param file - File to upload
 * @param processingMode - Processing mode ('immediate' or 'async')
 * @returns Result with upload response or error
 */
export async function uploadImportFile(
  uploadUrl: string,
  file: File,
  processingMode?: 'immediate' | 'async',
): Promise<ImportResult<UploadSuccessResponse>> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    if (processingMode === 'immediate') {
      headers['x-processing-mode'] = 'immediate';
    }

    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers,
    });

    const responseData = await response.json().catch(() => null);

    // Check for error response
    if (!response.ok || (responseData?.statusCode && responseData.statusCode !== 200)) {
      return {
        success: false,
        error: responseData?.message || `Upload failed with status ${response.status}`,
      };
    }

    // Check for success response
    if (responseData?.success === true) {
      return {
        success: true,
        data: {
          success: true,
          message: responseData.message,
          jobId: responseData.jobId,
          totalRows: responseData.totalRows,
        },
      };
    }

    return {
      success: false,
      error: 'Invalid response from upload server',
    };
  } catch (error) {
    console.error('Import upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload file',
    };
  }
}

/**
 * Execute a complete import (init + upload)
 *
 * @param importType - Type of import
 * @param body - Init request body
 * @param file - File to upload
 * @param additionalHeaders - Additional headers for init request
 * @returns Result with upload response or error
 */
export async function executeImport<T extends ImportInitRequestBody>(
  importType: string,
  body: T,
  file: File,
  additionalHeaders: Record<string, string | number> = {},
): Promise<ImportResult<UploadSuccessResponse>> {
  // Step 1: Initialize import
  const initResult = await initImport(importType, body, additionalHeaders);

  if (!initResult.success) {
    return initResult;
  }

  // Step 2: Upload file
  const uploadResult = await uploadImportFile(initResult.data.uploadUrl, file);

  return uploadResult;
}

// =============================================================================
// CONTACT IMPORT HELPERS
// =============================================================================

/**
 * Initialize a contact import
 *
 * @param listId - ID of the shop list
 * @param fieldMappings - Field mappings
 * @param consentAll - Optional consent options when consent is not mapped
 * @returns Result with upload URL or error
 */
export async function initContactImport(
  listId: string | number,
  fieldMappings: FieldMappingDto[],
  consentAll?: ConsentAllOptions,
  vapidKeyId?: string,
): Promise<ImportResult<InitResponseData>> {
  const body: ContactInitRequestBody = {
    id_shop_list: String(listId),
    field_mappings: fieldMappings,
  };

  // Only include consent_all if it has at least one property set to true
  if (consentAll && (consentAll.email || consentAll.sms || consentAll.whatsapp)) {
    body.consent_all = consentAll;
  }

  if (vapidKeyId) {
    body.id_shop_vapid_key = vapidKeyId;
  }

  return initImport('contact', body);
}

/**
 * Execute a complete contact import (init + upload)
 *
 * @param listId - ID of the shop list
 * @param fieldMappings - Field mappings
 * @param file - File to upload
 * @returns Result with upload response or error
 */
export async function executeContactImport(
  listId: string | number,
  fieldMappings: FieldMappingDto[],
  file: File,
): Promise<ImportResult<UploadSuccessResponse>> {
  const body: ContactInitRequestBody = {
    id_shop_list: String(listId),
    field_mappings: fieldMappings,
  };

  return executeImport('contact', body, file);
}

// =============================================================================
// CUSTOM DATA IMPORT HELPERS
// =============================================================================

/**
 * Initialize a custom data import
 *
 * @param customDataDefinitionId - ID of the custom data definition (schema)
 * @param fieldMappings - Field mappings
 * @returns Result with upload URL or error
 */
export async function initCustomDataImport(
  customDataDefinitionId: string | number,
  fieldMappings: FieldMappingDto[],
): Promise<ImportResult<InitResponseData>> {
  const body: CustomDataInitRequestBody = {
    id_custom_data_definition: String(customDataDefinitionId),
    field_mappings: fieldMappings,
  };

  return initImport('custom_data', body);
}

/**
 * Execute a complete custom data import (init + upload)
 *
 * @param customDataDefinitionId - ID of the custom data definition (schema)
 * @param fieldMappings - Field mappings
 * @param file - File to upload
 * @returns Result with upload response or error
 */
export async function executeCustomDataImport(
  customDataDefinitionId: string | number,
  fieldMappings: FieldMappingDto[],
  file: File,
): Promise<ImportResult<UploadSuccessResponse>> {
  const body: CustomDataInitRequestBody = {
    id_custom_data_definition: String(customDataDefinitionId),
    field_mappings: fieldMappings,
  };

  return executeImport('custom_data', body, file);
}

// =============================================================================
// SELECTION-BASED IMPORT HELPERS (campaign_automation, campaign_bulk, template)
// =============================================================================

export type SelectionImportType = 'campaign_automation' | 'campaign_bulk' | 'template';

/**
 * Initialize a selection-based import (campaigns, templates)
 *
 * @param importType - Type of import (campaign_automation, campaign_bulk, template)
 * @param selectedIds - Array of IDs to import
 * @returns Result with upload URL or error
 */
export async function initSelectionImport(
  importType: SelectionImportType,
  selectedIds: (string | number)[],
): Promise<ImportResult<InitResponseData>> {
  const body: SelectionInitRequestBody = {
    selected_ids: selectedIds,
  };

  return initImport(importType, body);
}
